import { trpc } from './trpc';
import { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@tagadapay/trpc-types';
import { toast } from 'sonner';
import i18n from '@/config/i18n';

type OutputRouter = inferRouterOutputs<AppRouter>;
type InputRouter = inferRouterInputs<AppRouter>;

// Output types
export type AccountPlanOutput = OutputRouter['accountPlans']['get'];
export type BillingPreviewOutput = OutputRouter['billingReports']['previewCurrent'];
export type TriggerManualBillingOutput = OutputRouter['accountManager']['triggerManualBilling'];

// Input types
export type TriggerManualBillingInput = InputRouter['accountManager']['triggerManualBilling'];
export type AccountStatusOutput = OutputRouter['accountManager']['getAccountStatus'];
export type UpdateAccountStatusInput = InputRouter['accountManager']['updateAccountStatus'];
export type ToggleChargeInput = InputRouter['accountManager']['toggleCharge'];
export type UpdatePriceInput = InputRouter['accountManager']['updatePrice'];
export type UpdatePercentageInput = InputRouter['accountManager']['updatePercentage'];
export type UpdateWeeklyAmountInput = InputRouter['accountManager']['updateWeeklyAmount'];

/**
 * Hook to search by any ID and auto-join organization (account managers only)
 */
export function useAccountManagerSearchAndJoin() {
  return trpc.accountManager.searchByAnyIdAndJoin.useMutation();
}

/**
 * Staff-only fallback when opening /org/{slug} without existing membership.
 * Does not affect merchants or the Fast Search quick-jump flow.
 */
export function useStaffOrgAutoJoinBySlug() {
  return trpc.accountManager.joinByOrgSlug.useMutation();
}

/**
 * Hook to fetch account plan for settings display
 */
export function useAccountPlanForSettings(planType: 'scale' | 'subclub' = 'scale') {
  return trpc.accountPlans.get.useQuery(
    { planType },
    {
      staleTime: 30000,
      retry: 2,
    }
  );
}

/**
 * Hook to fetch the per-field buy-rate floor for a merchant's pricing.
 *
 * Returns `{ grid, floor }` when the merchant's partner has a signed CRM
 * buy rate, or `null` for direct merchants / legacy partners (no floor).
 */
export function useAccountBuyRateFloor(accountId: string | undefined) {
  return trpc.accountManager.getAccountBuyRateFloor.useQuery(
    { accountId: accountId! },
    {
      enabled: !!accountId,
      staleTime: 30000,
    }
  );
}

/**
 * Hook to fetch billing preview for a custom date range
 */
export function useBillingPreview(periodStart?: string, periodEnd?: string) {
  return trpc.billingReports.previewCurrent.useQuery(
    {
      period: 'weekly',
      periodStart,
      periodEnd,
    },
    {
      enabled: true,
    }
  );
}

/**
 * Hook to trigger manual billing
 */
export function useTriggerManualBilling(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.triggerManualBilling.useMutation({
    onSuccess: (data) => {
      toast.success(`Billing triggered successfully! Report ID: ${data.reportId}`);
      utils.billingReports.list.invalidate();
      utils.billingReports.previewCurrent.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to trigger billing');
    },
  });
}

/**
 * Hook to toggle charge enablement for a product type
 */
export function useToggleCharge(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.toggleCharge.useMutation({
    onSuccess: (data) => {
      toast.success(
        `${data.enabled ? 'Enabled' : 'Disabled'} billing for ${data.productType.replace(/_/g, ' ')}`
      );
      utils.accountPlans.get.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to toggle charge');
    },
  });
}

/**
 * Hook to update fixed price for a usage type
 */
export function useUpdateFixedPrice(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.updatePrice.useMutation({
    onSuccess: (data) => {
      toast.success(`Updated price to ${formatMoney(data.amount)}`);
      utils.accountPlans.get.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update price');
    },
  });
}

/**
 * Hook to update percentage price for a product type
 */
export function useUpdatePercentagePrice(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.updatePercentage.useMutation({
    onSuccess: (data) => {
      toast.success(`Updated percentage to ${data.percentage.toFixed(2)}%`);
      utils.accountPlans.get.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update percentage');
    },
  });
}

/**
 * Hook to update the TagadaRx CRM fee (Tagada Rx transactions)
 */
export function useUpdateRxRate(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.updateRxRate.useMutation({
    onSuccess: (data) => {
      toast.success(`Updated TagadaRx rate to ${data.percentage.toFixed(2)}%`);
      utils.accountPlans.get.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update the TagadaRx rate');
    },
  });
}

/**
 * Hook to update weekly plan base amount
 */
export function useUpdateWeeklyAmount(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.updateWeeklyAmount.useMutation({
    onSuccess: () => {
      toast.success('Weekly plan amount updated successfully');
      utils.accountPlans.get.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update weekly amount');
    },
  });
}

/**
 * Hook to fetch organization metadata (private metadata for access controls)
 */
export function useOrganizationMetadata(accountId: string | undefined) {
  return trpc.accountManager.getOrganizationMetadata.useQuery(
    { accountId: accountId! },
    {
      enabled: !!accountId,
      staleTime: 30000,
    }
  );
}

/**
 * Hook to update organization metadata (access controls, feature flags, etc.)
 */
export function useUpdateOrganizationMetadata(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.updateOrganizationMetadata.useMutation({
    onSuccess: () => {
      toast.success('Organization settings updated successfully');
      utils.accountManager.getOrganizationMetadata.invalidate();
      utils.accountManager.getAccountStatus.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update organization settings');
    },
  });
}

/**
 * Hook to read combined account status for Account Manager.
 */
export function useAccountStatus(accountId: string | undefined) {
  return trpc.accountManager.getAccountStatus.useQuery(
    { accountId: accountId! },
    {
      enabled: !!accountId,
      staleTime: 30000,
    },
  );
}

export function useLiveSubscriptionCount(accountId: string | undefined, enabled: boolean) {
  return trpc.accountManager.countLiveSubscriptions.useQuery(
    { accountId: accountId! },
    { enabled: !!accountId && enabled },
  );
}

/**
 * Hook to disable/re-enable billing and CRM access.
 */
export function useUpdateAccountStatus(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.updateAccountStatus.useMutation({
    onSuccess: (data) => {
      toast.success(i18n.t('accountManager.accountStatus.toastSuccess', { ns: 'common' }));
      if (data.subscriptions) {
        const { action, count, remaining } = data.subscriptions;
        const message = i18n.t(`accountManager.accountStatus.subscriptionsToast.${action}`, {
          ns: 'common',
          count,
        });
        if (remaining === null) {
          toast.error(i18n.t('accountManager.accountStatus.subscriptionsToast.cancelFailed', { ns: 'common' }));
        } else if (remaining > 0) {
          toast.warning(
            `${message} ${i18n.t(`accountManager.accountStatus.subscriptionsToast.remaining.${action}`, { ns: 'common', count: remaining })}`,
          );
        } else {
          toast.info(message);
        }
      }
      utils.accountManager.countLiveSubscriptions.invalidate();
      utils.accountManager.getAccountStatus.invalidate();
      utils.accountManager.getOrganizationMetadata.invalidate();
      utils.accountPlans.get.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || i18n.t('accountManager.accountStatus.toastError', { ns: 'common' }));
    },
  });
}

// Helper function to format money
function formatMoney(amount: number, currency = 'usd') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
}

// =====================================
// Custom Fees Hooks
// =====================================

// Input types for custom fees
export type AddCustomFeeInput = InputRouter['accountManager']['addCustomFee'];
export type UpdateCustomFeeInput = InputRouter['accountManager']['updateCustomFee'];
export type ToggleCustomFeeInput = InputRouter['accountManager']['toggleCustomFee'];
export type DeleteCustomFeeInput = InputRouter['accountManager']['deleteCustomFee'];

/**
 * Hook to add a new custom fee to an account
 */
export function useAddCustomFee(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.addCustomFee.useMutation({
    onSuccess: (data) => {
      toast.success(`Added custom fee: ${data.fee.description}`);
      utils.accountPlans.get.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add custom fee');
    },
  });
}

/**
 * Hook to update an existing custom fee
 */
export function useUpdateCustomFee(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.updateCustomFee.useMutation({
    onSuccess: () => {
      toast.success('Custom fee updated successfully');
      utils.accountPlans.get.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update custom fee');
    },
  });
}

/**
 * Hook to toggle a custom fee enabled/disabled
 */
export function useToggleCustomFee(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.toggleCustomFee.useMutation({
    onSuccess: (data) => {
      toast.success(`Custom fee ${data.fee.enabled ? 'enabled' : 'disabled'}`);
      utils.accountPlans.get.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to toggle custom fee');
    },
  });
}

/**
 * Hook to permanently delete a custom fee
 */
export function useDeleteCustomFee(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.deleteCustomFee.useMutation({
    onSuccess: () => {
      toast.success('Custom fee deleted');
      utils.accountPlans.get.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete custom fee');
    },
  });
}

// =====================================
// AI Credits Hooks
// =====================================

export type AiCreditBalanceOutput = OutputRouter['accountManager']['getAiCreditBalance'];
export type GrantAiCreditsInput = InputRouter['accountManager']['grantAiCredits'];

/**
 * Hook to read an account's AI-credit balance + recent ledger.
 */
export function useAiCreditBalance(accountId: string | undefined) {
  return trpc.accountManager.getAiCreditBalance.useQuery(
    { accountId: accountId! },
    {
      enabled: !!accountId,
      staleTime: 30000,
    },
  );
}

/**
 * Hook to grant / claw back AI credits on an account.
 */
export function useGrantAiCredits(onSuccess?: () => void) {
  const utils = trpc.useUtils();

  return trpc.accountManager.grantAiCredits.useMutation({
    onSuccess: () => {
      toast.success(i18n.t('accountManager.aiCredits.toastSuccess', { ns: 'common' }));
      utils.accountManager.getAiCreditBalance.invalidate();
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || i18n.t('accountManager.aiCredits.toastError', { ns: 'common' }));
    },
  });
}

// =====================================
// Product Sync Hooks
// =====================================

export type ResetProductSyncCounterInput = InputRouter['accountManager']['resetProductSyncCounter'];

/**
 * Hook to reset a merchant's manual product-sync rate limit (Shopify + WooCommerce).
 */
export function useResetProductSyncCounter(onSuccess?: () => void) {
  return trpc.accountManager.resetProductSyncCounter.useMutation({
    onSuccess: (data) => {
      // cleared === 0 means nothing was over the limit (or only genuinely-recent
      // in-flight jobs remain) — surface it as info, not a green "unblocked".
      if (data.cleared === 0) {
        toast.info(i18n.t('accountManager.productSync.toastNoop', { ns: 'common' }));
      } else {
        toast.success(
          i18n.t('accountManager.productSync.toastSuccess', { ns: 'common', count: data.cleared }),
        );
      }
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      toast.error(error.message || i18n.t('accountManager.productSync.toastError', { ns: 'common' }));
    },
  });
}
